/**
 * Post-GPT guard rails for composite / takeaway meals.
 * GPT often returns ONE anchor (e.g. small chips = 500 cal) and drops other components.
 * This layer applies pattern-matched floors before writing to Supabase.
 * Keep in sync with mactrack-ios/utils/nutritionMealGuards.ts
 */

export interface MacroTotals {
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  calories: number;
}

interface MealFloor extends MacroTotals {
  trapCalories?: number;
  trapTolerance?: number;
}

interface MealGuardRule {
  id: string;
  all?: RegExp[];
  any?: RegExp[];
  exclude?: RegExp[];
  floor: MealFloor | ((text: string) => MealFloor);
}

const SNACK_EXCLUDES = [
  /\bsnack\b/i,
  /\bsmall\b/i,
  /\blight\b/i,
  /\bjust a\b/i,
  /\bonly a\b/i,
  /\bcoffee\b/i,
  /\btea\b(?!.*\b(curry|meal|dinner|lunch)\b)/i,
];

const USER_MACRO_EXCLUDES = [
  /\d+\s*g\s*protein/i,
  /\d+\s*g\s*carbs/i,
  /\d+\s*calories/i,
  /\d+\s*cal\b/i,
  /\d+\s*kcal\b/i,
];

const PROTEIN = /\b(chicken|beef|steak|fish|cod|haddock|salmon|tuna|egg|bacon|sausage|turkey|lamb|pork|tofu|prawn|shrimp|meat|nando|shawarma|kebab|tikka|masala|bolognese|protein|grilled)\b/i;
const CARBS = /\b(rice|pasta|spaghetti|noodles|chips|fries|chippy|bread|toast|naan|potato|wedges|pizza|wrap|tortilla|burrito|granola|porridge|oats|pilau)\b/i;
const TAKEAWAY = /\b(takeaway|take away|chippy|chip shop|restaurant|deliveroo|uber eats|just eat|nandos|nando's|kfc|dominos|domino's|greggs|subway)\b/i;
const LARGE = /\b(large|big|full|hearty|takeaway portion|meal deal)\b/i;

const MEAL_GUARD_RULES: MealGuardRule[] = [
  {
    id: 'fish_and_chips',
    all: [/\b(chips|fries|chippy)\b/i],
    any: [/\b(fish|cod|haddock|battered)\b/i],
    exclude: SNACK_EXCLUDES,
    floor: (text) =>
      LARGE.test(text)
        ? { protein_g: 33, carbs_g: 110, fat_g: 46, calories: 1150, trapCalories: 500, trapTolerance: 80 }
        : { protein_g: 32, carbs_g: 95, fat_g: 40, calories: 1050, trapCalories: 500, trapTolerance: 80 },
  },
  {
    id: 'sausage_and_chips',
    all: [/\b(chips|fries)\b/i, /\b(sausage|battered sausage)\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 14, carbs_g: 85, fat_g: 48, calories: 900, trapCalories: 500, trapTolerance: 80 },
  },
  {
    id: 'steak_and_chips',
    all: [PROTEIN, /\b(chips|fries|steak)\b/i],
    any: [/\bsteak\b/i],
    exclude: SNACK_EXCLUDES,
    floor: (text) =>
      LARGE.test(text)
        ? { protein_g: 55, carbs_g: 85, fat_g: 50, calories: 1100 }
        : { protein_g: 50, carbs_g: 70, fat_g: 42, calories: 950 },
  },
  {
    id: 'chicken_and_rice',
    all: [/\bchicken\b/i, /\brice\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 55, carbs_g: 56, fat_g: 8, calories: 590, trapCalories: 380, trapTolerance: 50 },
  },
  {
    id: 'curry_and_rice',
    all: [/\b(curry|tikka|masala|korma|jalfrezi|balti)\b/i, /\brice\b/i],
    exclude: [...SNACK_EXCLUDES, /\bnaan\b/i],
    floor: { protein_g: 35, carbs_g: 70, fat_g: 22, calories: 650 },
  },
  {
    id: 'curry_and_naan',
    all: [/\b(curry|tikka|masala|korma|jalfrezi|balti)\b/i, /\bnaan\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 35, carbs_g: 60, fat_g: 26, calories: 700 },
  },
  {
    id: 'tikka_full_meal',
    all: [/\b(tikka|masala|curry)\b/i],
    any: [/\b(rice|naan|pilau)\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 40, carbs_g: 90, fat_g: 28, calories: 900 },
  },
  {
    id: 'pasta_bolognese',
    all: [/\b(pasta|spaghetti|penne|rigatoni)\b/i],
    any: [/\b(bolognese|meat sauce|ragu|ragù)\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 35, carbs_g: 75, fat_g: 20, calories: 650, trapCalories: 130, trapTolerance: 40 },
  },
  {
    id: 'pasta_lunch',
    all: [/\bpasta\b/i],
    exclude: [...SNACK_EXCLUDES, /\b(bolognese|meat sauce|carbonara|pesto)\b/i],
    floor: { protein_g: 15, carbs_g: 65, fat_g: 5, calories: 400, trapCalories: 130, trapTolerance: 40 },
  },
  {
    id: 'loaded_fries',
    all: [/\b(fries|chips)\b/i],
    any: [/\b(cheese|bacon|sour cream|loaded|dirty)\b/i],
    exclude: [...SNACK_EXCLUDES, /\b(fish|cod|haddock)\b/i],
    floor: { protein_g: 20, carbs_g: 55, fat_g: 38, calories: 750, trapCalories: 340, trapTolerance: 80 },
  },
  {
    id: 'burger_and_fries',
    all: [/\b(burger|big mac|quarter pounder|cheeseburger)\b/i, /\b(fries|chips)\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 30, carbs_g: 85, fat_g: 48, calories: 900, trapCalories: 340, trapTolerance: 80 },
  },
  {
    id: 'full_english',
    any: [/\bfull english\b/i, /\bfull breakfast\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 40, carbs_g: 55, fat_g: 65, calories: 1000 },
  },
  {
    id: 'burrito_bowl',
    all: [/\b(burrito|bowl)\b/i],
    any: [/\b(rice|beans|guac|guacamole|chicken)\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 40, carbs_g: 85, fat_g: 28, calories: 850 },
  },
  {
    id: 'pad_thai_takeaway',
    all: [/\b(pad thai|noodles)\b/i],
    any: [/\b(takeaway|take away|chicken|prawn|shrimp)\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 30, carbs_g: 80, fat_g: 22, calories: 680 },
  },
  {
    id: 'caesar_with_chicken',
    all: [/\bcaesar\b/i, /\b(chicken|grilled)\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 40, carbs_g: 15, fat_g: 35, calories: 600 },
  },
  {
    id: 'avocado_toast_full',
    all: [/\b(avocado|avo)\b/i, /\b(toast|sourdough|bread)\b/i],
    exclude: SNACK_EXCLUDES,
    floor: { protein_g: 12, carbs_g: 45, fat_g: 35, calories: 550, trapCalories: 160, trapTolerance: 60 },
  },
  {
    id: 'meal_prep_multiplier',
    all: [/\b(meal prep|containers?|portions?)\b/i, /\bx\s*\d+\b|\d+\s*x\b|\d+\s*(containers?|portions?|meals?)\b/i],
    exclude: SNACK_EXCLUDES,
    floor: (text) => {
      const match =
        text.match(/\bx\s*(\d+)\b/i) ||
        text.match(/\b(\d+)\s*x\b/i) ||
        text.match(/\b(\d+)\s*(?:containers?|portions?|meals?)\b/i);
      const count = match ? Math.min(Number.parseInt(match[1], 10) || 1, 5) : 1;
      return {
        protein_g: 55 * count,
        carbs_g: 56 * count,
        fat_g: 8 * count,
        calories: 590 * count,
      };
    },
  },
];

const CHICKEN_RICE_TRAP = {
  protein_g: 31,
  carbs_g: 28,
  tolerance: 5,
  floor: { protein_g: 55, carbs_g: 56, fat_g: 8, calories: 590 },
};

function matchesRule(text: string, rule: MealGuardRule): boolean {
  if (rule.exclude?.some((re) => re.test(text))) return false;
  if (USER_MACRO_EXCLUDES.some((re) => re.test(text))) return false;
  if (rule.all?.length && !rule.all.every((re) => re.test(text))) return false;
  if (rule.any?.length && !rule.any.some((re) => re.test(text))) return false;
  if (!rule.all?.length && !rule.any?.length) return false;
  return true;
}

function resolveFloor(text: string, rule: MealGuardRule): MealFloor {
  return typeof rule.floor === 'function' ? rule.floor(text) : rule.floor;
}

function applyFloor(data: MacroTotals, floor: MealFloor): MacroTotals {
  return {
    protein_g: Math.max(data.protein_g, floor.protein_g),
    carbs_g: Math.max(data.carbs_g, floor.carbs_g),
    fat_g: Math.max(data.fat_g, floor.fat_g),
    calories: Math.max(data.calories, floor.calories),
  };
}

function isNearTrap(calories: number, trap?: number, tolerance = 50): boolean {
  if (trap == null) return false;
  return Math.abs(calories - trap) <= tolerance;
}

function applyChickenRiceTrap(data: MacroTotals, text: string): MacroTotals {
  if (!/\bchicken\b/i.test(text) || !/\brice\b/i.test(text)) return data;
  const { protein_g, carbs_g, tolerance, floor } = CHICKEN_RICE_TRAP;
  const matchesTrap =
    Math.abs(data.protein_g - protein_g) <= tolerance &&
    Math.abs(data.carbs_g - carbs_g) <= tolerance;
  if (matchesTrap || data.calories < 450) {
    return applyFloor(data, floor);
  }
  return data;
}

function applyMultiComponentGuard(data: MacroTotals, text: string): MacroTotals {
  if (SNACK_EXCLUDES.some((re) => re.test(text))) return data;
  if (USER_MACRO_EXCLUDES.some((re) => re.test(text))) return data;

  const hasProtein = PROTEIN.test(text);
  const hasCarbs = CARBS.test(text);
  const hasTakeaway = TAKEAWAY.test(text);
  const conjunction = /\b\w+\s+(and|with|plus)\s+\w+\b/i.test(text);

  if (!hasProtein || !hasCarbs) return data;

  let minCalories = 500;
  if (hasTakeaway || LARGE.test(text)) minCalories = 650;
  if (conjunction && hasTakeaway) minCalories = 750;

  if (data.calories >= minCalories) return data;

  const boostedCalories = Math.max(minCalories, Math.round(data.calories * 1.75));
  const ratio = data.calories > 0 ? boostedCalories / data.calories : 1.75;

  return {
    protein_g: Math.max(data.protein_g, Math.round(data.protein_g * ratio)),
    carbs_g: Math.max(data.carbs_g, Math.round(data.carbs_g * ratio)),
    fat_g: Math.max(data.fat_g, Math.round(data.fat_g * ratio * 1.2)),
    calories: boostedCalories,
  };
}

export function applyNutritionGuards(
  data: MacroTotals,
  mealText: string
): MacroTotals & { guardApplied?: string[] } {
  const text = mealText.trim();
  if (!text) return data;

  const applied: string[] = [];
  let result = { ...data };

  for (const rule of MEAL_GUARD_RULES) {
    if (!matchesRule(text, rule)) continue;

    const floor = resolveFloor(text, rule);
    const shouldApply =
      result.calories < floor.calories ||
      isNearTrap(result.calories, floor.trapCalories, floor.trapTolerance ?? 50);

    if (shouldApply) {
      result = applyFloor(result, floor);
      applied.push(rule.id);
    }
  }

  const beforeTrap = { ...result };
  result = applyChickenRiceTrap(result, text);
  if (result.calories !== beforeTrap.calories) applied.push('chicken_rice_trap');

  if (applied.length === 0) {
    const beforeGeneric = { ...result };
    result = applyMultiComponentGuard(result, text);
    if (result.calories !== beforeGeneric.calories) applied.push('multi_component');
  }

  return applied.length > 0 ? { ...result, guardApplied: applied } : result;
}

export type EstimateConfidence = 'high' | 'medium' | 'low';

export function scaleMacroTotals(data: MacroTotals, multiplier: number): MacroTotals {
  const round = (n: number) => Math.round(n * multiplier);
  return {
    protein_g: round(data.protein_g),
    carbs_g: round(data.carbs_g),
    fat_g: round(data.fat_g),
    calories: round(data.calories),
  };
}

export function getEstimateConfidence(
  mealText: string,
  data: MacroTotals,
  guardApplied?: string[]
): EstimateConfidence {
  if (USER_MACRO_EXCLUDES.some((re) => re.test(mealText))) return 'high';
  if (/\b(mcdonald|big mac|quarter pounder|nando'?s)\b/i.test(mealText)) return 'high';

  if (guardApplied?.includes('multi_component')) return 'low';
  if (guardApplied && guardApplied.length > 0) return 'medium';

  const isSnack = SNACK_EXCLUDES.some((re) => re.test(mealText));
  if (!isSnack && data.calories < 300) return 'low';
  if (TAKEAWAY.test(mealText) || (PROTEIN.test(mealText) && CARBS.test(mealText))) {
    return 'medium';
  }

  return 'high';
}
